import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import type { OnChangeFn, SortingState } from "@tanstack/react-table";
import { snakeCase } from "lodash";
import useCapabilities from "@/hooks/useCapabilities";
import usePagination from "@/hooks/usePagination";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import { TaskStageValues } from "@/types";
import { TASKS_QUERY, type TasksInput, type TaskOrderByInput } from "../queries";

const DEFAULT_TASKS_TABLE_SORT: SortingState = [{ id: "dueDate", desc: false }];
const DEFAULT_STAGE = TaskStageValues.OPEN;

interface UseTasksQueryOptions {
  fixedDepartment?: string;
  fixedUserId?: string;
  myTasks?: boolean;
  useTeamTagForDepartment?: boolean;
  showUnassignedChip?: boolean;
}

export default function useTasksQuery({
  fixedDepartment,
  fixedUserId,
  myTasks = false,
  useTeamTagForDepartment,
  showUnassignedChip = false
}: UseTasksQueryOptions = {}) {
  const { id: currentUserId } = useCapabilities();
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const urlParams = useParams();
  const { params, addSearchParam, deleteSearchParams } = urlParams;
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({ paramName: "search" });

  const stageParam = params.stage as string | undefined;
  const defaultApplied = useRef(false);

  useEffect(() => {
    if (defaultApplied.current) return;
    defaultApplied.current = true;
    if (!stageParam) {
      addSearchParam("stage", DEFAULT_STAGE);
    }
    if (!params.sortField && DEFAULT_TASKS_TABLE_SORT.length > 0) {
      const sort = DEFAULT_TASKS_TABLE_SORT[0];
      addSearchParam("sortField", sort.id);
      addSearchParam("sortDir", sort.desc ? "desc" : "asc");
    }
  }, [addSearchParam, params.sortField, stageParam]);

  const stage = stageParam;
  const department = params.department as string | undefined;
  const assignedToMe = params.assignedToMe === "true";
  const createdByMe = params.createdByMe === "true";
  const overdue = params.overdue === "true";
  const unassigned = showUnassignedChip && params.unassigned === "true";

  function setStage(value: string | undefined) {
    if (value) {
      addSearchParam("stage", value);
    } else {
      deleteSearchParams("stage");
    }
  }

  function setDepartment(value: string | undefined) {
    if (value) {
      addSearchParam("department", value);
    } else {
      deleteSearchParams("department");
    }
  }

  function setAssignedToMe(value: boolean) {
    if (value) {
      addSearchParam("assignedToMe", "true");
      deleteSearchParams("unassigned");
    } else {
      deleteSearchParams("assignedToMe");
    }
  }

  function setCreatedByMe(value: boolean) {
    if (value) {
      addSearchParam("createdByMe", "true");
    } else {
      deleteSearchParams("createdByMe");
    }
  }

  function setOverdue(value: boolean) {
    if (value) {
      addSearchParam("overdue", "true");
    } else {
      deleteSearchParams("overdue");
    }
  }

  function setUnassigned(value: boolean) {
    if (value) {
      addSearchParam("unassigned", "true");
      deleteSearchParams("assignedToMe");
    } else {
      deleteSearchParams("unassigned");
    }
  }

  const dueBy = (params.dueBy as string) || undefined;

  function setDueBy(value: string | undefined) {
    if (value) {
      addSearchParam("dueBy", value);
    } else {
      deleteSearchParams("dueBy");
    }
  }

  const input = useMemo((): TasksInput => {
    const result: TasksInput = {};
    if (debouncedSearch) result.search = debouncedSearch;
    if (stage) result.stage = [stage as TasksInput["stage"] extends (infer U)[] | undefined ? U : never];
    const effectiveDepartment = fixedDepartment || department;
    if (effectiveDepartment) {
      if (useTeamTagForDepartment) {
        result.teamTag = effectiveDepartment;
      } else {
        result.assignedToDepartment = effectiveDepartment;
      }
    }
    if (myTasks) {
      result.myTasks = true;
      if (unassigned) result.unassigned = true;
    } else if (fixedUserId) {
      result.assignedToId = fixedUserId;
    } else if (unassigned) {
      result.unassigned = true;
    } else if (assignedToMe && currentUserId) {
      result.assignedToId = currentUserId;
    }
    if (createdByMe && currentUserId) result.authorId = currentUserId;
    if (overdue) result.overdue = true;
    if (dueBy) result.dueDateRange = { to: dueBy };
    return result;
  }, [
    debouncedSearch,
    stage,
    department,
    fixedDepartment,
    fixedUserId,
    myTasks,
    useTeamTagForDepartment,
    assignedToMe,
    unassigned,
    createdByMe,
    currentUserId,
    overdue,
    dueBy
  ]);

  const [orderBy, setOrderBy] = useOrderByParam(DEFAULT_TASKS_TABLE_SORT, urlParams);

  const orderByVariable = useMemo((): TaskOrderByInput | undefined => {
    const sort = orderBy[0];
    if (!sort) return undefined;
    return {
      field: snakeCase(sort.id).toUpperCase(),
      direction: sort.desc ? "DESC" : "ASC"
    };
  }, [orderBy]);

  const prevDepsRef = useRef({
    debouncedSearch,
    stage,
    department,
    assignedToMe,
    unassigned,
    createdByMe,
    overdue,
    dueBy
  });

  useEffect(() => {
    const prev = prevDepsRef.current;
    const changed =
      prev.debouncedSearch !== debouncedSearch ||
      prev.stage !== stage ||
      prev.department !== department ||
      prev.assignedToMe !== assignedToMe ||
      prev.unassigned !== unassigned ||
      prev.createdByMe !== createdByMe ||
      prev.overdue !== overdue ||
      prev.dueBy !== dueBy;

    if (changed) {
      resetPagination();
    }

    prevDepsRef.current = {
      debouncedSearch,
      stage,
      department,
      assignedToMe,
      unassigned,
      createdByMe,
      overdue,
      dueBy
    };
  }, [debouncedSearch, stage, department, assignedToMe, unassigned, createdByMe, overdue, dueBy, resetPagination]);

  const { data, loading } = useQuery(TASKS_QUERY, {
    variables: {
      ...paginationVariables,
      input: Object.keys(input).length > 0 ? input : undefined,
      orderBy: orderByVariable
    },
    errorPolicy: "all"
  });

  const hasActiveFilters =
    !!debouncedSearch || !!stage || !!department || assignedToMe || unassigned || createdByMe || overdue || !!dueBy;

  return {
    data,
    loading,
    searchValue,
    setSearchValue,
    clearSearch,
    createHeaderPagination,
    createFooterPagination,
    stage,
    setStage,
    department,
    setDepartment,
    assignedToMe,
    setAssignedToMe,
    createdByMe,
    setCreatedByMe,
    overdue,
    setOverdue,
    unassigned,
    setUnassigned,
    showUnassignedChip,
    dueBy,
    setDueBy,
    orderBy,
    setOrderBy,
    hasActiveFilters
  };
}

function useOrderByParam(
  defaultWhenEmpty: SortingState,
  url: ReturnType<typeof useParams>
): [SortingState, OnChangeFn<SortingState>] {
  const { params, addSearchParam, deleteSearchParams } = url;
  const orderByFromUrl = useMemo((): SortingState => {
    const sortField = params.sortField as string | undefined;
    const sortDir = params.sortDir as string | undefined;
    if (!sortField) return [];
    return [{ id: sortField, desc: sortDir === "desc" }];
  }, [params.sortField, params.sortDir]);

  const orderBy = useMemo(
    () => (orderByFromUrl.length > 0 ? orderByFromUrl : defaultWhenEmpty),
    [orderByFromUrl, defaultWhenEmpty]
  );

  const setOrderBy: OnChangeFn<SortingState> = (updaterOrValue) => {
    const next = typeof updaterOrValue === "function" ? updaterOrValue(orderBy) : updaterOrValue;
    const sort = next[0];
    if (sort) {
      addSearchParam("sortField", sort.id);
      addSearchParam("sortDir", sort.desc ? "desc" : "asc");
    } else {
      deleteSearchParams(["sortField", "sortDir"]);
    }
  };

  return [orderBy, setOrderBy];
}
